import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.scss";
import { PlusIcon } from "@kohler-dsm-library/kohler-icons";
import { HiChevronRight, HiChevronLeft, HiChevronDown } from "react-icons/hi2";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { applyLocaleToUrl } from "@/utils/formatters";
import { SLICK_CAROUSEL_PRODUCT_HEADING } from "@/constants";

const NextArrow = props => {
  const { className, style, onClick, slickprodClass = "" } = props;
  let slickArrowClass = `${className.replace("slick-arrow", "")} ${slickprodClass}`;
  return (
    <div className={slickArrowClass} style={{ ...style }} onClick={onClick}>
      <HiChevronRight size={50} color="#fff" />
    </div>
  );
};

const PrevArrow = props => {
  const { className, style, onClick, slickprodClass = "" } = props;
  let slickArrowClass = `${className.replace("slick-arrow", "")} ${slickprodClass}`;
  return (
    <div className={slickArrowClass} style={{ ...style }} onClick={onClick}>
      <HiChevronLeft size={50} color="#fff" />
    </div>
  );
};

const SlickCarousel = ({ images = [], heading = "" }) => {
  const { locale } = useRouter();
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showProducts, setShowProducts] = useState(true);
  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 980);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const slickArrowPositionClass =
    showProducts && isMobile ? "slick-arrow-with-product" : "slick-arrow";

  const defaultSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow slickprodClass={slickArrowPositionClass} />,
    prevArrow: <PrevArrow slickprodClass={slickArrowPositionClass} />,
  };

  const handleHotspotClick = spot => {
    setActiveHotspot(spot);
  };

  const handleHotspotEnter = fieldObj => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setActiveHotspot(fieldObj);
  };

  const handleHotspotLeave = () => {
    hideTimeoutRef.current = setTimeout(() => setActiveHotspot(null), 200);
  };

  const handleModalEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  };

  const handleModalLeave = () => {
    hideTimeoutRef.current = setTimeout(() => setActiveHotspot(null), 200);
  };

  return (
    <div className={styles.wrapper}>
      {heading && <h2 className="title">{heading}</h2>}
      <Slider {...defaultSettings}>
        {images?.map(img => {
          const {
            fields: {
              hotspots = [],
              title = "",
              description = "",
              src: {
                fields: { file: { url = "" } = {}, title: alt = "" } = {},
              } = {},
            } = {},
          } = img;
          const {
            sku: activeHotspotSku = "",
            x: xCoords,
            y: yCoords,
            label: activeHotspotLabel = "",
            link1: {
              fields: { url: link1Url = "", label: link1Text = "" } = {},
            } = {},
            link2: {
              fields: { url: link2Url = "", label: link2Text = "" } = {},
            } = {},
            hotspotImage: {
              fields: {
                file: { url: activeUrl = "" } = {},
                title: activeTitle = "",
              } = {},
            } = {},
          } = activeHotspot || {};

          return (
            <>
              <div
                className="image-container"
                key={img.src}
                onClick={() => activeHotspot && setActiveHotspot(null)}
              >
                <Image className="main-image" src={url} alt={alt} fill />
                {!isMobile && (
                  <div className="image-caption">
                    <h3 className="caption-title">{title}</h3>
                    <p className="caption-desc">{description}</p>
                  </div>
                )}

                {!isMobile &&
                  hotspots?.map(spot => {
                    const {
                      fields: { x = 0, y = 0, label = "" } = {},
                      fields: fieldObj = {},
                    } = spot;
                    return (
                      <div
                        key={label}
                        className="hotspot-point"
                        style={{
                          top: `${y}%`,
                          left: `${x}%`,
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          handleHotspotClick(fieldObj);
                        }}
                        onMouseEnter={() => handleHotspotEnter(fieldObj)}
                        onMouseLeave={handleHotspotLeave}
                        title={label}
                      >
                        <div className="hotspot-icon">
                          <PlusIcon size="small" className="plusIcon" />
                        </div>
                      </div>
                    );
                  })}

                {!isMobile && activeHotspotSku && (
                  <div
                    className="hotspot-modal"
                    onMouseEnter={handleModalEnter}
                    onMouseLeave={handleModalLeave}
                    onClick={e => e.stopPropagation()}
                    style={{
                      top: `${yCoords < 30 ? `${yCoords}% + 5px` : `${yCoords}%`}`,
                      left: `${xCoords > 60 ? `calc(${xCoords}% - 425px)` : `calc(${xCoords}% + 25px)`}`,
                    }}
                  >
                    <div className="modal-image-wrapper">
                      <Image
                        src={activeUrl}
                        alt={activeTitle}
                        className="modal-image"
                        fill
                      />
                    </div>
                    <div className="modal-content">
                      {activeHotspotLabel && (
                        <p className="modal-content-title">
                          {activeHotspotLabel}
                        </p>
                      )}
                      <a
                        className="modal-content-link1"
                        href={applyLocaleToUrl(link1Url, locale)}
                      >
                        {link1Text}
                      </a>
                      {activeHotspotSku && (
                        <span className="modal-content-sku">
                          {activeHotspotSku}
                        </span>
                      )}
                      <Link
                        href={applyLocaleToUrl(link2Url, locale)}
                        className="modal-content-link2"
                      >
                        {link2Text}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              {/* MOBILE CONTENT */}
              {isMobile && (
                <div className="mobile-content">
                  <h3 className="mobile-title">{img?.fields?.title}</h3>
                  <p className="mobile-desc">{img?.fields?.description}</p>
                  <div
                    className="featured-toggle"
                    onClick={() => setShowProducts(!showProducts)}
                    style={{
                      borderBottom: !showProducts ? "1px solid #666" : "none",
                    }}
                  >
                    <p className="featured-title">{SLICK_CAROUSEL_PRODUCT_HEADING[locale]}</p>
                    <span className="toggle-icon">
                      {showProducts ? (
                        <HiChevronDown size={25} />
                      ) : (
                        <HiChevronRight size={25} />
                      )}
                    </span>
                  </div>
                  {showProducts && img?.fields?.hotspots && (
                    <div className="featured-products">
                      {img?.fields?.hotspots?.map(spot => (
                        <div key={spot.label} className="product-card">
                          <div className="product-card-imageWrapper">
                            <Image
                              className="product-card-image"
                              src={
                                "https:" +
                                spot?.fields?.hotspotImage?.fields?.file?.url
                              }
                              alt={
                                spot?.fields?.hotspotImage?.fields?.file?.title
                              }
                              fill
                            />
                          </div>
                          <div className="product-card-content">
                            <p>{spot?.fields?.label}</p>
                            <a
                              className="product-card-link"
                              href={applyLocaleToUrl(
                                spot?.fields?.link1?.fields?.url,
                                locale
                              )}
                            >
                              {spot?.fields?.link1?.fields?.label}
                            </a>
                            <span className="product-card-sku">
                              {spot?.fields?.sku}
                            </span>
                            <Link
                              href={
                                applyLocaleToUrl(
                                  spot?.fields?.link2?.fields?.url,
                                  locale
                                ) || ""
                              }
                              locale={false}
                              className="product-card-store-locator"
                            >
                              {spot?.fields?.link2?.fields?.label}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })}
      </Slider>
    </div>
  );
};

SlickCarousel.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })
  ),
  settings: PropTypes.object,
};

export default SlickCarousel;
